import { useEffect, useState } from 'react';
import type { MailJobSummary } from '@rb/shared/mail/job.types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { mailSendWorkerUrlOf, resolveVerseEntry } from '@rb/shared/admin-verses';
import { getMailRewardOptions } from '@/client/admin.client';
import { confirmMail, previewMail } from '@/client/mail-worker.client';
import {
  compileGroups,
  type MailFormCommon,
  type RewardGroup,
} from '@/components/admin/mail-send/compile-groups';
import { RewardGroupBuilder } from '@/components/admin/mail-send/RewardGroupBuilder';
import type { RewardOption } from '@/components/admin/mail/reward-options';
import { useActiveVerseStore } from '@/verse/active-verse.store';

function genJobId(): string {
  return `mail-${crypto.randomUUID().slice(0, 8)}`;
}

function emptyCommon(): MailFormCommon {
  return { jobId: genJobId(), groupType: '', titleKey: '', contentKey: '', ttlDays: 30 };
}

export default function MailSendPage() {
  const activeVerseId = useActiveVerseStore((s) => s.activeVerseId);
  const verse = activeVerseId ? resolveVerseEntry(activeVerseId) : null;
  const workerUrl = verse ? mailSendWorkerUrlOf(verse.env, verse.flavor) : null;
  const canSend = workerUrl !== null;

  const [options, setOptions] = useState<RewardOption[]>([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [common, setCommon] = useState<MailFormCommon>(emptyCommon);
  const [groups, setGroups] = useState<RewardGroup[]>([{ rewards: [], accounts: [] }]);

  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState<{ previewId: string; summary: MailJobSummary } | null>(
    null
  );
  const [confirmResult, setConfirmResult] = useState<{ jobId: string; enqueued: number } | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMailRewardOptions()
      .then(setOptions)
      .catch((e) => setOptionsError(e instanceof Error ? e.message : String(e)));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeVerseId 변경 시에만 초기화
  useEffect(() => {
    setPreview(null);
    setConfirmResult(null);
    setError(null);
  }, [activeVerseId]);

  const patchCommon = (patch: Partial<MailFormCommon>) => {
    setCommon((c) => ({ ...c, ...patch }));
    setPreview(null);
  };

  const handleGroupsChange = (next: RewardGroup[]) => {
    setGroups(next);
    setPreview(null);
  };

  const onPreview = async () => {
    setError(null);
    setConfirmResult(null);
    const c = compileGroups(common, groups);
    if (!c.ok) {
      setError(c.error);
      return;
    }
    const dispatchVerseId = activeVerseId;
    setPreviewing(true);
    try {
      const result = await previewMail(workerUrl!, c.def);
      // verse 전환됨 — stale 응답 폐기 (오조준 방지)
      if (useActiveVerseStore.getState().activeVerseId !== dispatchVerseId) return;
      setPreview(result);
    } catch (e) {
      if (useActiveVerseStore.getState().activeVerseId !== dispatchVerseId) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewing(false);
    }
  };

  const onConfirm = async () => {
    if (!preview) return;
    setError(null);
    setConfirming(true);
    const dispatchVerseId = activeVerseId;
    try {
      const result = await confirmMail(workerUrl!, preview.previewId);
      // verse 전환됨 — stale 응답 폐기 (오조준 방지)
      if (useActiveVerseStore.getState().activeVerseId !== dispatchVerseId) return;
      setConfirmResult(result);
      setPreview(null);
    } catch (e) {
      if (useActiveVerseStore.getState().activeVerseId !== dispatchVerseId) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setConfirming(false);
    }
  };

  const busy = previewing || confirming;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-bold">메일 전송</h1>

      {!verse && (
        <Alert variant="destructive">
          <AlertDescription>verse 를 먼저 선택하세요 (좌측 verse 셀렉터).</AlertDescription>
        </Alert>
      )}
      {verse && !canSend && (
        <Alert variant="destructive">
          <AlertDescription>
            이 env({verse.env})엔 메일 워커가 없습니다. staging/prod verse 에서만 발송 가능합니다.
          </AlertDescription>
        </Alert>
      )}
      {verse && canSend && (
        <Alert>
          <AlertDescription>
            발송 대상: <span className="font-medium">{verse.label}</span> ·{' '}
            {workerUrl.replace(/^https?:\/\//, '')}
          </AlertDescription>
        </Alert>
      )}

      {optionsError && (
        <Alert variant="destructive">
          <AlertDescription>보상 옵션 로드 실패: {optionsError}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">공통 정보</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="mail-job-id">Job ID</Label>
            <Input
              id="mail-job-id"
              value={common.jobId}
              onChange={(e) => patchCommon({ jobId: e.target.value })}
              disabled={busy}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="mail-group-type">그룹 타입</Label>
            <Input
              id="mail-group-type"
              value={common.groupType}
              onChange={(e) => patchCommon({ groupType: e.target.value })}
              placeholder="예: LEAGUE_SWEEP"
              disabled={busy}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="mail-title-key">제목 l10n 키</Label>
            <Input
              id="mail-title-key"
              value={common.titleKey}
              onChange={(e) => patchCommon({ titleKey: e.target.value })}
              placeholder="MAIL_..."
              disabled={busy}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="mail-content-key">본문 l10n 키</Label>
            <Input
              id="mail-content-key"
              value={common.contentKey}
              onChange={(e) => patchCommon({ contentKey: e.target.value })}
              placeholder="MAIL_..."
              disabled={busy}
            />
          </div>
          <div className="flex w-24 flex-col gap-1">
            <Label htmlFor="mail-ttl">TTL (일)</Label>
            <Input
              id="mail-ttl"
              type="number"
              min={1}
              value={common.ttlDays}
              onChange={(e) => patchCommon({ ttlDays: Number(e.target.value) || 0 })}
              disabled={busy}
            />
          </div>
        </CardContent>
      </Card>

      <RewardGroupBuilder
        options={options}
        groups={groups}
        onChange={handleGroupsChange}
        disabled={busy}
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {preview && (
        <Alert>
          <AlertDescription>
            <p>
              대상 {preview.summary.recipients}명 (중복 제외{' '}
              {preview.summary.skippedDuplicateAddresses}) · 보상 합계{' '}
              {Object.entries(preview.summary.rewardTotals)
                .map(([k, v]) => `${k}×${v}`)
                .join(', ') || '-'}
            </p>
            <p className="mt-1 font-medium">
              Slack 에 미리보기가 게시되었습니다. 확인 후 발송하세요.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {confirmResult && (
        <Alert>
          <AlertDescription>
            발송 확정됨 — Job {confirmResult.jobId}, {confirmResult.enqueued}건 큐에 등록됨.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2">
        <Button type="button" onClick={() => void onPreview()} disabled={busy || !canSend}>
          {previewing ? '미리보기 생성 중…' : '미리보기'}
        </Button>
        <Button
          type="button"
          variant="default"
          onClick={() => void onConfirm()}
          disabled={busy || !preview || !canSend}
        >
          {confirming ? '발송 중…' : '발송 확정'}
        </Button>
      </div>
    </div>
  );
}
